import React from 'react';
import PropTypes from 'prop-types';
import { Skeleton, Row, Col, Card, Typography, Tabs } from 'antd';
import styled from 'styled-components';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const SkeletonWrapper = styled.div`
  background-color: #f0f2f5;
  border-radius: 10px;
`;

const SkeletonCard = styled(Card)`
  margin-bottom: 16px;
  .ant-card-body {
    padding: 12px;
  }
`;

const SkeletonRow = ({ index }) => (
  <Row gutter={[16, 16]} style={{ marginTop: '16px' }} data-testid={`skeleton-row-${index}`}>
    {Array.from({ length: 6 }).map(() => {
      const uniqueKey = `skeleton-row-${crypto.getRandomValues(new Uint32Array(1))[0].toString(36)}`;
      return (
        <Col
          xs={24}
          md={4}
          key={`skeleton-row-${index}-col-${uniqueKey}`}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <Skeleton.Input style={{ width: '100%', height: '36px' }} active />
        </Col>
      );
    })}
  </Row>
);

const VehicleManagementDetailSkeleton = ({ activeTab }) => {
  return (
    <SkeletonWrapper>
      {/* Plaka ve Limit Bilgisi */}
      <Row gutter={[16, 16]} data-testid="skeleton-input">
        <Col xs={24} md={16} style={{ display: 'flex', flexDirection: 'column' }}>
          <SkeletonCard bordered={false}>
            <Row gutter={[16, 16]}>
              {['1', '2', '3'].map((key) => (
                <Col
                  xs={24}
                  md={8}
                  key={`skeleton-card-${key}`}
                  style={{ display: 'flex', flexDirection: 'column' }}
                >
                  <Skeleton.Input
                    style={{ width: '100%', height: '48px' }}
                    active
                    data-testid="skeleton-input"
                  />
                </Col>
              ))}
            </Row>
          </SkeletonCard>
        </Col>
        <Col xs={24} md={8} style={{ display: 'flex', flexDirection: 'column' }}>
          <SkeletonCard bordered={false}>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={24} style={{ display: 'flex', flexDirection: 'column' }}>
                <Skeleton.Input style={{ width: '100%', height: '48px' }} active />
              </Col>
            </Row>
          </SkeletonCard>
        </Col>
      </Row>

      {/* Araç Bilgileri ve Sürücü Bilgileri */}
      <Row gutter={[16, 16]}>
        <Col xs={24} md={16} style={{ display: 'flex', flexDirection: 'column' }}>
          <SkeletonCard bordered={false}>
            <Row gutter={[16, 16]}>
              {['1', '2'].map((key) => (
                <Col
                  xs={24}
                  md={12}
                  key={`skeleton-info-${key}`}
                  style={{ display: 'flex', flexDirection: 'column' }}
                >
                  <Skeleton.Input
                    style={{ width: '100%', height: '36px', marginBottom: '8px' }}
                    active
                  />
                  <Skeleton.Input
                    style={{ width: '100%', height: '36px', marginBottom: '8px' }}
                    active
                  />
                  <Skeleton.Input
                    style={{ width: '100%', height: '36px', marginBottom: '8px' }}
                    active
                  />
                </Col>
              ))}
            </Row>
          </SkeletonCard>
        </Col>
        <Col xs={24} md={8} style={{ display: 'flex', flexDirection: 'column' }}>
          <SkeletonCard bordered={false}>
            <Row gutter={[16, 16]}>
              <Col xs={24} style={{ display: 'flex', flexDirection: 'column' }}>
                <Skeleton.Input
                  style={{ width: '100%', height: '36px', marginBottom: '8px' }}
                  active
                />
                <Skeleton.Input
                  style={{ width: '100%', height: '36px', marginBottom: '8px' }}
                  active
                />
                <Skeleton.Input
                  style={{ width: '100%', height: '36px', marginBottom: '8px' }}
                  active
                />
              </Col>
            </Row>
          </SkeletonCard>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
        <Col span={24}>
          <SkeletonCard bordered={false}>
            <Tabs
              activeKey={activeTab}
              tabBarStyle={{ border: '0' }}
              style={{ paddingTop: '15px', backgroundColor: '#fff', borderRadius: '10px' }}
            >
              <TabPane
                tab={<Skeleton.Input style={{ width: '250px', height: '36px' }} active />}
                key="1"
              >
                {['1', '2'].map(() => {
                  const uniqueKey = `multiple-tab-${crypto.getRandomValues(new Uint32Array(1))[0].toString(36)}`;
                  return (
                    <div key={uniqueKey}>
                      {/* Sağlayıcı */}
                      <Row gutter={[16, 16]} style={{ marginTop: '16px', marginBottom: '36px' }}>
                        {['1'].map(() => {
                          const providerKey = `provider-${crypto.getRandomValues(new Uint32Array(1))[0].toString(36)}`;
                          return (
                            <Col key={providerKey}>
                              <Skeleton.Avatar shape="circle" size={64} active />
                            </Col>
                          );
                        })}
                        <Col>
                          <Skeleton.Input
                            style={{ width: '200px', height: '36px', marginTop: '16px' }}
                            active
                          />
                        </Col>
                      </Row>
                      {/* Form Alanları */}
                      <Row gutter={[16, 16]} style={{ marginTop: '16px', gap: 10 }}>
                        {['1', '2', '3'].map(() => {
                          const formFieldKey = `form-field-${crypto.getRandomValues(new Uint32Array(1))[0].toString(36)}`;
                          return (
                            <Col
                              xs={24}
                              md={6}
                              key={formFieldKey}
                              style={{ display: 'flex', flexDirection: 'column' }}
                            >
                              <Skeleton.Input style={{ width: '100%', height: '36px' }} active />
                            </Col>
                          );
                        })}
                      </Row>
                      {/* Gün Seçimi */}
                      <SkeletonCard style={{ marginTop: '24px' }}>
                        <Row style={{ marginTop: '16px' }}>
                          <Skeleton.Input
                            style={{ width: '250px', height: '32px', marginBottom: '16px' }}
                            active
                          />
                        </Row>
                        <Row
                          gutter={[16, 16]}
                          style={{ marginTop: '16px', gap: 10 }}
                          data-testid="skeleton-button"
                        >
                          {Array.from({ length: 7 }).map(() => {
                            const buttonKey = `button-${crypto.getRandomValues(new Uint32Array(1))[0].toString(36)}`;
                            return (
                              <Col
                                xs={8}
                                md={3}
                                key={buttonKey}
                                style={{ display: 'flex', flexDirection: 'column' }}
                              >
                                <Skeleton.Button
                                  style={{
                                    width: '100%',
                                    height: '40px',
                                    border: 'solid 1px #00a483',
                                    borderRadius: '6px',
                                  }}
                                  active
                                  data-testid="skeleton-button"
                                />
                              </Col>
                            );
                          })}
                        </Row>
                      </SkeletonCard>
                    </div>
                  );
                })}
              </TabPane>

              <TabPane
                tab={<Skeleton.Input style={{ width: '250px', height: '36px' }} active />}
                key="2"
              >
                {Array.from({ length: 10 }).map((_, index) => {
                  // `index` parametresi eklendi
                  const uniqueKey = `tab2-row-${crypto.getRandomValues(new Uint32Array(1))[0].toString(36)}`;
                  return <SkeletonRow key={uniqueKey} index={index} />;
                })}
              </TabPane>

              <TabPane
                tab={<Skeleton.Input style={{ width: '250px', height: '36px' }} active />}
                key="3"
              >
                {Array.from({ length: 10 }).map((_, index) => {
                  // `index` parametresi eklendi
                  const uniqueKey = `tab3-row-${crypto.getRandomValues(new Uint32Array(1))[0].toString(36)}`;
                  return <SkeletonRow key={uniqueKey} index={index} />;
                })}
              </TabPane>
            </Tabs>
          </SkeletonCard>
        </Col>
      </Row>
    </SkeletonWrapper>
  );
};

VehicleManagementDetailSkeleton.propTypes = {
  activeTab: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default VehicleManagementDetailSkeleton;
